import { useMachine } from '@xstate/react';
import { FaroMetaUser } from 'common-rum';
import { assign, createMachine } from 'xstate';

import { User } from './user';

export type UserState = (
  | { name: 'loading' }
  | { name: 'success'; user: User }
  | { name: 'error' }
) & { invalidate: () => void; update: (user: User) => void };

export function useUserState({
  getUser,
  on,
  setUser,
}: Parameters<typeof createUserMachine>[0]): UserState {
  const [state, send] = useMachine(() =>
    createUserMachine({
      getUser,
      on,
      setUser,
    }),
  );

  return state.matches('error')
    ? {
        name: 'error',
        invalidate: () => {
          send({ type: 'get user' });
        },
        update: (user: User) => {
          send({ type: 'update user', user });
        },
      }
    : state.matches('success')
    ? {
        name: 'success',
        user: state.context.user,
        invalidate: () => {
          send({ type: 'get user' });
        },
        update: (user: User) => {
          send({ type: 'update user', user });
        },
      }
    : {
        name: 'loading',
        invalidate: () => {
          send({ type: 'get user' });
        },
        update: (user: User) => {
          send({ type: 'update user', user });
        },
      };
}

type UserMachineContext = {
  user?: User;
};

type UserMachineEvent =
  | { type: 'get user' }
  | { type: 'got user'; user: User }
  | { type: 'failed to get user' }
  | { type: 'update user'; user: User };

type UserMachineState =
  | {
      value: 'loading' | 'error';
      context: { user: undefined };
    }
  | {
      value: 'success';
      context: { user: User };
    };

function createUserMachine({
  getUser,
  on,
  setUser,
}: {
  getUser: () => Promise<User>;
  on: {
    gotUser: (user: User) => void;
    failedToGetUser: () => void;
  };
  setUser: (user?: FaroMetaUser) => void;
}) {
  return createMachine<UserMachineContext, UserMachineEvent, UserMachineState>(
    {
      id: 'user',
      predictableActionArguments: true,
      preserveActionOrder: true,
      initial: 'loading',
      on: {
        'get user': {
          target: 'loading',
        },
        'update user': {
          actions: ['store user'],
        },
      },
      states: {
        loading: {
          invoke: {
            src: 'getUser',
          },
          on: {
            'got user': {
              actions: ['store user'],
              target: 'success',
            },
            'failed to get user': {
              actions: ['clear user'],
              target: 'error',
            },
          },
        },
        error: {
          entry: ['on error'],
        },
        success: {
          entry: ['on success'],
        },
      },
    },
    {
      actions: {
        'on success': (context, event) => {
          if (event.type === 'got user') {
            on.gotUser(event.user);
            try {
              const user = {
                username: event.user.name,
                id: `${event.user.ssoUserId}`,
              };
              setUser(user);
            } catch (e) {
              console.error(e);
            }
          }
        },
        'on error': () => on.failedToGetUser(),
        'store user': assign({
          user: (context, event) =>
            event.type === 'got user' || event.type === 'update user'
              ? event.user
              : context.user,
        }),
        'clear user': assign({
          // eslint-disable-next-line
          user: (context) => undefined,
        }),
      },
      services: {
        getUser: () => async (send) => {
          try {
            const user = await getUser();

            send({ type: 'got user', user });
          } catch (error) {
            console.error('Failed to get user:', error);
            send({ type: 'failed to get user' });
          }
        },
      },
    },
  );
}
